import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { FREE_FAVORITES_LIMIT } from '@/constants/premiumLimits';

const LOCAL_STORAGE_KEY = 'baby-food-favorites';

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user, isPremium } = useAuth();

  useEffect(() => {
    if (user) {
      fetchFavoritesFromSupabase();
    } else {
      const localFavorites = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (localFavorites) {
        try {
          const parsed = JSON.parse(localFavorites);
          setFavorites(Array.isArray(parsed) ? parsed : []);
        } catch (error) {
          console.error('Error parsing local favorites:', error);
          setFavorites([]);
        }
      }
    }
  }, [user]);

  const fetchFavoritesFromSupabase = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await (supabase as any)
        .from('favorite_foods')
        .select('food_id')
        .eq('user_id', user.id);

      if (error) throw error;

      setFavorites(data?.map((fav: any) => fav.food_id) || []);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast.error('Kunne ikke hente favoritter');
    } finally {
      setIsLoading(false);
    }
  };

  const saveFavoritesToLocalStorage = (newFavorites: string[]) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newFavorites));
  };

  const isAtLimit = !isPremium && favorites.length >= FREE_FAVORITES_LIMIT;

  const canAddFavorite = (foodId: string) => {
    if (favorites.includes(foodId)) return true; // can always remove
    if (isPremium) return true;
    return favorites.length < FREE_FAVORITES_LIMIT;
  };

  const addToFavorites = async (foodId: string) => {
    if (user) {
      try {
        const { error } = await (supabase as any)
          .from('favorite_foods')
          .insert({ user_id: user.id, food_id: foodId });

        if (error) throw error;

        setFavorites(prev => [...prev, foodId]);
        toast.success('Lagt til i favoritter');
      } catch (error) {
        console.error('Error adding favorite:', error);
        toast.error('Kunne ikke legge til favoritt');
      }
    } else {
      const newFavorites = [...favorites, foodId];
      setFavorites(newFavorites);
      saveFavoritesToLocalStorage(newFavorites);
      toast.success('Lagt til i favoritter');
    }
  };

  const removeFromFavorites = async (foodId: string) => {
    if (user) {
      try {
        const { error } = await (supabase as any)
          .from('favorite_foods')
          .delete()
          .eq('user_id', user.id)
          .eq('food_id', foodId);

        if (error) throw error;

        setFavorites(prev => prev.filter(id => id !== foodId));
        toast.success('Fjernet fra favoritter');
      } catch (error) {
        console.error('Error removing favorite:', error);
        toast.error('Kunne ikke fjerne favoritt');
      }
    } else {
      const newFavorites = favorites.filter(id => id !== foodId);
      setFavorites(newFavorites);
      saveFavoritesToLocalStorage(newFavorites);
      toast.success('Fjernet fra favoritter');
    }
  };

  const toggleFavorite = async (foodId: string): Promise<boolean> => {
    if (favorites.includes(foodId)) {
      await removeFromFavorites(foodId);
      return true;
    } else {
      if (!canAddFavorite(foodId)) {
        return false; // signal that limit was hit
      }
      await addToFavorites(foodId);
      return true;
    }
  };

  const isFavorite = (foodId: string) => favorites.includes(foodId);

  return {
    favorites,
    isLoading,
    toggleFavorite,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    isAtLimit,
    canAddFavorite
  };
}
